import { type ReactNode, useEffect, useState, memo } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Skeleton from "./skeleton";
import { Tooltip, TooltipProvider } from "./ui/tooltip";
import IconChatBubble from "./icons/icon-chat-bubble";
import IconMailSend from "./icons/icon-mail-send";
import IconUserFeedbackHeart from "./icons/icon-user-feedback-heart";
import IconColorSwatches from "./icons/icon-color-swatches";
import IconUserCircle from "./icons/icon-user-circle";
import IconReceipt from "./icons/icon-receipt";
import IconScrewDriver from "./icons/icon-screw-driver";
import IconAlignTop from "./icons/icon-align-top";
import { useBrands, type Brand, type BrandResponse, type SubscriptionStatus, BrandModal } from "@/modules/brand";
import { useAuth } from "@/modules/auth";
import IconTag from "./icons/icon-tag";
import IconHeart from "./icons/icon-heart";
import IconExpandWindow from "./icons/icon-expand-window";
import IconCodeMonitor from "./icons/icon-code-monitor";
import IconChatBubbleSquare from "./icons/icon-chat-bubble-square";
import IconDashboardGauge from "./icons/icon-dashboard-gauge";
import { useNavigationGuard } from "@/contexts/navigation-guard-context";
import SubscriptionBanner from "./subscription-banner";

// States that should show a subscription indicator
const subscriptionBannerStates: SubscriptionStatus[] = ["trial_active", "trial_expired", "free_plan", "subscription_cancelling", "subscription_cancelled", "subscription_past_due", "subscription_expired"];

// Config for collapsed subscription indicator
const collapsedIndicatorConfig: Record<Exclude<SubscriptionStatus, "subscription_active" | "subscription_lifetime">, { bgColor: string; borderColor: string; iconColor: string }> = {
	trial_active: { bgColor: "bg-amber-50", borderColor: "border-amber-200", iconColor: "text-amber-600" },
	trial_expired: { bgColor: "bg-red-50", borderColor: "border-red-200", iconColor: "text-red-600" },
	free_plan: { bgColor: "bg-blue-50", borderColor: "border-blue-200", iconColor: "text-blue-600" },
	subscription_cancelling: { bgColor: "bg-orange-50", borderColor: "border-orange-200", iconColor: "text-orange-600" },
	subscription_cancelled: { bgColor: "bg-red-50", borderColor: "border-red-200", iconColor: "text-red-600" },
	subscription_past_due: { bgColor: "bg-red-50", borderColor: "border-red-200", iconColor: "text-red-600" },
	subscription_expired: { bgColor: "bg-red-50", borderColor: "border-red-200", iconColor: "text-red-600" },
};

interface CollapsedSubscriptionIndicatorProps {
	brand: Brand;
	workspaceId: string;
}

function CollapsedSubscriptionIndicator({ brand, workspaceId }: CollapsedSubscriptionIndicatorProps) {
	const [isOpen, setIsOpen] = useState(false);

	// Don't show indicator for active, lifetime, or undefined status
	if (!brand.subscription_status || !subscriptionBannerStates.includes(brand.subscription_status)) {
		return null;
	}

	const config = collapsedIndicatorConfig[brand.subscription_status as keyof typeof collapsedIndicatorConfig];

	if (!config) return null;

	const renderIcon = () => {
		if (brand.subscription_status === "free_plan") {
			return (
				<svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
					<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
				</svg>
			);
		}

		return (
			<svg className={`h-5 w-5 ${config.iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
				<path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
			</svg>
		);
	};

	return (
		<DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					className={`flex items-center justify-center w-10 h-10 rounded-lg ${config.bgColor} border ${config.borderColor} cursor-pointer transition-colors hover:opacity-80`}
					title="Subscription status"
				>
					{renderIcon()}
				</button>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content className="w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50 p-2" sideOffset={8} side="right" align="start">
					<SubscriptionBanner brand={brand} workspaceId={workspaceId} />
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

interface SidebarItemProps {
	icon: ReactNode;
	label: string;
	active?: boolean;
	to?: string;
	collapsed?: boolean;
}

const SidebarItem = memo(function SidebarItem({ icon, label, active, to, collapsed }: SidebarItemProps) {
	const navigate = useNavigate();
	const { confirmNavigation } = useNavigationGuard();

	const handleClick = () => {
		if (to) {
			if (confirmNavigation()) {
				navigate(to);
			}
		}
	};

	const button = (
		<button
			onClick={handleClick}
			className={`flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors cursor-pointer select-none ${active ? "bg-brand/5 text-brand" : "text-secondary hover:bg-gray-50"} ${collapsed ? "justify-center" : ""}`}
		>
			<span className="text-lg shrink-0">{icon}</span>
			{!collapsed && <span className="font-medium font-heading ">{label}</span>}
		</button>
	);

	if (collapsed) {
		return (
			<Tooltip content={label} side="right" sideOffset={10}>
				{button}
			</Tooltip>
		);
	}

	return button;
});

interface UserProfileProps {
	first_name: string;
	last_name: string;
	email: string | null;
	avatar?: string | null;
	collapsed?: boolean;
}

function UserProfile({ first_name, last_name, email, avatar, collapsed }: UserProfileProps) {
	const navigate = useNavigate();
	const { logout } = useAuth();
	const [isOpen, setIsOpen] = useState(false);

	const handleLogout = async () => {
		// Implement logout logic here
		try {
			await logout();
			navigate("/login", { replace: true });
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	return (
		<DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
			<DropdownMenu.Trigger asChild>
				<button
					type="button"
					id="user-profile-dropdown-trigger"
					className={`flex w-full items-center gap-3 rounded-lg py-1 px-3 h-13.5 transition-colors hover:bg-gray-50 cursor-pointer outline-none ${collapsed ? "justify-center" : ""}`}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsOpen(!isOpen);
					}}
					title={collapsed ? `${first_name} ${last_name}` : undefined}
				>
					<div className="flex h-8 w-8 items-center justify-center rounded-full bg-linear-to-br from-purple-400 to-pink-400 shrink-0">
						{avatar ? (
							<img src={avatar} alt={`${first_name} ${last_name}`} className="h-full w-full rounded-full" referrerPolicy="no-referrer" />
						) : (
							<span className="text-sm font-semibold text-white">{first_name?.charAt(0) || ""}</span>
						)}
					</div>
					{!collapsed && (
						<>
							<div className="flex-1 text-left">
								<div className="text-sm font-medium text-primary truncate w-33.75">{`${first_name} ${last_name}`}</div>
								<div className="text-t-11 text-tertiary truncate w-33.75">{email}</div>
							</div>
							<svg className="h-4 w-4 text-gray-400 " fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
							</svg>
						</>
					)}
				</button>
			</DropdownMenu.Trigger>

			<DropdownMenu.Portal>
				<DropdownMenu.Content className="min-w-55 bg-white rounded-lg p-1.5 shadow-lg border border-gray-200 z-50" sideOffset={8} side="top" align="start">
					<DropdownMenu.Item
						className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-100 focus:bg-gray-100 transition-colors"
						onSelect={() => {
							setIsOpen(false);
							navigate("/profile");
						}}
					>
						<svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
						</svg>
						My Profile
					</DropdownMenu.Item>
					<DropdownMenu.Separator className="h-px bg-gray-200 my-1.5" />
					<DropdownMenu.Item
						className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 rounded-md cursor-pointer outline-none hover:bg-red-50 focus:bg-red-50 transition-colors"
						onSelect={handleLogout}
					>
						<svg className="h-4 w-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
						</svg>
						Logout
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}

interface BrandHeaderProps {
	name?: string;
	trialEndsText?: string;
	currentWorkspaceId?: string;
	collapsed?: boolean;
}

function BrandHeader({ name, trialEndsText, currentWorkspaceId, collapsed }: BrandHeaderProps) {
	const navigate = useNavigate();
	const { brands, setSelectedBrand } = useBrands();
	const { updateProfile, setWorkspaceId } = useAuth();
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	// Brands are already loaded by WorkspaceMemberGuard, just use the store directly
	const isLoading = !brands;

	// Always derive current brand from brands array using currentWorkspaceId
	// This ensures immediate update without flash when switching brands
	const currentBrand = brands?.find((b) => b.id === currentWorkspaceId);

	const handleBrandCreated = (brand: Brand | BrandResponse) => {
		// Convert BrandResponse to Brand if needed
		const brandData: Brand = {
			...brand,
			slug: brand.slug || "",
			role: (brand as Brand).role || "admin",
			accepted_at: (brand as Brand).accepted_at || new Date().toISOString(),
			timezone: (brand as Brand).timezone || "UTC",
			status: (brand as Brand).status || "active",
			website_url: brand.website_url || "",
		};
		setSelectedBrand(brandData);
		setWorkspaceId(brand.id);
		navigateToSelectedBrand(brandData);
	};

	const navigateToSelectedBrand = (brand: Brand) => {
		// Always navigate to dashboard when switching brands
		navigate(`/${brand.id}/dashboard`);
	};

	const handleBrandSwitch = (brand: Brand, event?: Event) => {
		if (event) {
			event.preventDefault();
		}

		// Batch all synchronous updates together before any async work
		setIsDropdownOpen(false);
		setSelectedBrand(brand);
		setWorkspaceId(brand.id);
		navigateToSelectedBrand(brand);

		// Fire and forget - update profile in background
		// updateProfile will also set currentWorkspaceId but we've already set it above
		// so it will be a no-op for that field
		updateProfile({ current_workspace_id: brand.id });
	};

	const displayName = currentBrand?.name || name;

	return (
		<>
			<DropdownMenu.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
				<DropdownMenu.Trigger asChild>
					<button
						className={`flex items-center rounded-lg transition-colors hover:bg-gray-50 cursor-pointer outline-none h-9 ${collapsed ? "w-full justify-center px-2" : "w-full gap-3 px-3"}`}
						title={collapsed ? displayName : undefined}
					>
						{isLoading ? (
							<>
								<Skeleton circle width={collapsed ? 32 : 20} height={collapsed ? 32 : 20} />
								{!collapsed && (
									<>
										<div className="flex-1 text-left">
											<Skeleton width={120} height={14} />
										</div>
										<Skeleton circle width={16} height={16} />
									</>
								)}
							</>
						) : (
							<>
								<div className="shrink-0">
									{currentBrand?.logo_url ? (
										<img className="w-5 h-5 rounded object-cover" src={currentBrand.logo_url} alt={displayName} />
									) : (
										<div className="w-5 h-5 text-xs rounded bg-gray-200 flex items-center justify-center font-medium text-gray-600">
											{currentBrand?.name?.charAt(0).toUpperCase()}
										</div>
									)}
								</div>
								{!collapsed && (
									<>
										<div className="flex-1 text-left w-44" title={displayName}>
											<div className="text-sm font-semibold text-gray-900 truncate">{displayName}</div>
											{trialEndsText && (
												<div className="flex items-center gap-1 text-xs text-orange-600">
													<svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
														<path
															fillRule="evenodd"
															d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
															clipRule="evenodd"
														/>
													</svg>
													<span className="font-medium text-t-11">{trialEndsText}</span>
												</div>
											)}
										</div>
										<svg className="h-4 w-4 text-gray-400 " fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
										</svg>
									</>
								)}
							</>
						)}
					</button>
				</DropdownMenu.Trigger>

				<DropdownMenu.Portal>
					<DropdownMenu.Content className="min-w-55 bg-white rounded-lg p-1.5 shadow-lg border border-gray-200 z-50" sideOffset={8} align={collapsed ? "start" : "end"} side="right">
						<div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide">Brands</div>
						{!brands || brands.length === 0 ? (
							<div className="px-3 py-2 text-sm text-gray-500">No brands found</div>
						) : (
							brands.map((brand) => (
								<DropdownMenu.Item
									key={brand.id}
									className={`flex items-center gap-3 px-3 py-2 text-sm rounded-md cursor-pointer outline-none hover:bg-gray-100 focus:bg-gray-100 transition-colors ${
										currentBrand?.id === brand.id ? "bg-gray-50" : ""
									}`}
									onSelect={(event) => {
										event.preventDefault();
										handleBrandSwitch(brand);
									}}
								>
									<div className="shrink-0">
										{brand.logo_url ? (
											<img className="w-5 h-5 rounded object-cover" src={brand.logo_url} alt={brand.name} />
										) : (
											<div className="w-5 h-5 rounded bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">{brand.name.charAt(0).toUpperCase()}</div>
										)}
									</div>
									<div className="flex-1 min-w-0">
										<div className="text-sm font-medium text-gray-900 truncate max-w-32">{brand.name}</div>
										{brand.website_url && <div className="text-xs text-gray-500 truncate">{brand.website_url}</div>}
									</div>
									{currentBrand?.id === brand.id && (
										<svg className="h-4 w-4 text-brand shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
									)}
								</DropdownMenu.Item>
							))
						)}
						<DropdownMenu.Separator className="h-px bg-gray-200 my-1.5" />
						<DropdownMenu.Item
							className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md cursor-pointer outline-none hover:bg-gray-100 focus:bg-gray-100 transition-colors"
							onSelect={() => navigate("/brands")}
						>
							<svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
								/>
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
							</svg>
							Manage Brands
						</DropdownMenu.Item>
						<DropdownMenu.Item
							className="flex items-center gap-2 px-3 py-2 text-sm text-brand rounded-md cursor-pointer outline-none hover:bg-brand/5 focus:bg-brand/5 transition-colors"
							onSelect={() => setIsCreateModalOpen(true)}
						>
							<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
							</svg>
							Create Brand
						</DropdownMenu.Item>
					</DropdownMenu.Content>
				</DropdownMenu.Portal>
			</DropdownMenu.Root>

			<BrandModal mode="create" open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen} onSuccess={handleBrandCreated} />
		</>
	);
}

interface AdminMenuProps {
	currentWorkspaceId: string | undefined;
	pathname: string;
	isCollapsed: boolean;
}

const AdminMenu = memo(function AdminMenu({ currentWorkspaceId, pathname, isCollapsed }: AdminMenuProps) {
	return (
		<div className="space-y-0.5">
			<SidebarItem
				icon={<IconUserCircle solid={pathname === `/${currentWorkspaceId}/teams`} />}
				label="Teams"
				to={`${currentWorkspaceId}/teams`}
				active={pathname === `/${currentWorkspaceId}/teams`}
				collapsed={isCollapsed}
			/>
			<SidebarItem
				icon={<IconReceipt solid={pathname === `/${currentWorkspaceId}/billing`} />}
				label="Billing"
				to={`${currentWorkspaceId}/billing`}
				active={pathname === `/${currentWorkspaceId}/billing`}
				collapsed={isCollapsed}
			/>
			<SidebarItem
				icon={<IconScrewDriver solid={pathname === `/${currentWorkspaceId}/settings`} />}
				label="Settings"
				to={`${currentWorkspaceId}/settings`}
				active={pathname === `/${currentWorkspaceId}/settings`}
				collapsed={isCollapsed}
			/>
		</div>
	);
});

export default function Sidebar() {
	const location = useLocation();
	const params = useParams<{ brandUrl: string }>();
	const { user } = useAuth();
	const { brands } = useBrands();

	// Use URL parameter as the source of truth for current workspace
	// This updates immediately on navigation, avoiding flash from async state updates
	const workspaceId = params.brandUrl;

	// Always derive current brand from brands array using the URL workspace ID
	const currentBrand = brands?.find((b) => b.id === workspaceId);

	// Derive isAdmin directly from the current brand's role
	// Role is already available on the Brand object loaded by WorkspaceMemberGuard
	const isAdmin = currentBrand?.role === "admin";

	// Collapse state with localStorage persistence
	const [isCollapsed, setIsCollapsed] = useState(() => {
		const saved = localStorage.getItem("sidebar-collapsed");
		return saved ? JSON.parse(saved) : false;
	});

	useEffect(() => {
		localStorage.setItem("sidebar-collapsed", JSON.stringify(isCollapsed));
		// Dispatch custom event for same-tab listeners
		window.dispatchEvent(new CustomEvent("sidebar-collapse-change", { detail: { isCollapsed } }));
	}, [isCollapsed]);

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	// Don't render until user is authenticated and brands are loaded
	// This prevents flash of incorrect menu items on page refresh
	if (!user || !brands || !currentBrand) {
		return null;
	}

	return (
		<TooltipProvider>
			<aside className={`flex h-screen flex-col border-r border-gray-200 bg-white relative z-10 shadow-[1px_0px_4px_0px_#00000006] transition-all duration-300 ${isCollapsed ? "w-16" : "w-64"}`}>
				{/* Floating collapse button */}
				<button
					onClick={toggleCollapse}
					className="absolute top-5 -right-3 z-20 p-1 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
					title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
				>
					<svg className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
					</svg>
				</button>

				{/* Header */}
				<div className={`border-b border-gray-200 p-4`}>
					<BrandHeader currentWorkspaceId={workspaceId} collapsed={isCollapsed} />
				</div>

				{/* Main Navigation */}
				<nav className="flex-1 space-y-4 overflow-y-auto p-4">
					<div className="space-y-0.5">
						<SidebarItem
							icon={<IconDashboardGauge solid={location.pathname === `/${workspaceId}` || location.pathname === `/${workspaceId}/dashboard`} />}
							label="Dashboard"
							to={`${workspaceId}/dashboard`}
							active={location.pathname === `/${workspaceId}` || location.pathname === `/${workspaceId}/dashboard`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconChatBubble solid={location.pathname === `/${workspaceId}/reviews`} />}
							label="Reviews"
							to={`${workspaceId}/reviews`}
							active={location.pathname === `/${workspaceId}/reviews`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconMailSend solid={location.pathname === `/${workspaceId}/forms`} />}
							label="Forms"
							to={`${workspaceId}/forms`}
							active={location.pathname === `/${workspaceId}/forms`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconUserFeedbackHeart solid={location.pathname === `/${workspaceId}/import`} />}
							label="Import"
							to={`${workspaceId}/import`}
							active={location.pathname === `/${workspaceId}/import`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconTag solid={location.pathname === `/${workspaceId}/tags`} />}
							label="Tags"
							to={`${workspaceId}/tags`}
							active={location.pathname === `/${workspaceId}/tags`}
							collapsed={isCollapsed}
						/>
					</div>

					<div className="space-y-0.5">
						<SidebarItem
							icon={<IconColorSwatches solid={location.pathname === `/${workspaceId}/branding`} />}
							label="Branding"
							to={`${workspaceId}/branding`}
							active={location.pathname === `/${workspaceId}/branding`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconHeart solid={location.pathname === `/${workspaceId}/wall-of-love`} />}
							label="Wall of Love"
							to={`${workspaceId}/wall-of-love`}
							active={location.pathname === `/${workspaceId}/wall-of-love`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconCodeMonitor solid={location.pathname === `/${workspaceId}/widgets`} />}
							label="Widgets"
							to={`${workspaceId}/widgets`}
							active={location.pathname === `/${workspaceId}/widgets`}
							collapsed={isCollapsed}
						/>
						<SidebarItem
							icon={<IconExpandWindow solid={location.pathname === `/${workspaceId}/shares`} />}
							label="Shares"
							to={`${workspaceId}/shares`}
							active={location.pathname === `/${workspaceId}/shares`}
							collapsed={isCollapsed}
						/>
					</div>

					{isAdmin && <AdminMenu currentWorkspaceId={workspaceId} pathname={location.pathname} isCollapsed={isCollapsed} />}

					<div className="space-y-0.5">
						{isCollapsed ? (
							<Tooltip content="Feedback" side="right" sideOffset={10}>
								<a
									href="https://sayaboutus.featurebase.app/"
									target="_blank"
									rel="noopener noreferrer"
									className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors cursor-pointer select-none text-secondary hover:bg-gray-50 justify-center"
								>
									<span className="text-lg shrink-0">
										<IconChatBubbleSquare />
									</span>
								</a>
							</Tooltip>
						) : (
							<a
								href="https://sayaboutus.featurebase.app/"
								target="_blank"
								rel="noopener noreferrer"
								className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors cursor-pointer select-none text-secondary hover:bg-gray-50"
							>
								<span className="text-lg shrink-0">
									<IconChatBubbleSquare />
								</span>
								<span className="font-medium font-heading ">Feedback</span>
							</a>
						)}
						{isCollapsed ? (
							<Tooltip content="Roadmap" side="right" sideOffset={10}>
								<a
									href="https://sayaboutus.featurebase.app/roadmap"
									target="_blank"
									rel="noopener noreferrer"
									className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors cursor-pointer select-none text-secondary hover:bg-gray-50 justify-center"
								>
									<span className="text-lg shrink-0">
										<IconAlignTop />
									</span>
								</a>
							</Tooltip>
						) : (
							<a
								href="https://sayaboutus.featurebase.app/roadmap"
								target="_blank"
								rel="noopener noreferrer"
								className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm transition-colors cursor-pointer select-none text-secondary hover:bg-gray-50"
							>
								<span className="text-lg shrink-0">
									<IconAlignTop />
								</span>
								<span className="font-medium font-heading ">Roadmap</span>
							</a>
						)}
					</div>
				</nav>

				{/* Subscription Banner */}
				{currentBrand && workspaceId && !isCollapsed && (
					<div className="p-4">
						<SubscriptionBanner brand={currentBrand} workspaceId={workspaceId} />
					</div>
				)}

				{/* Collapsed Subscription Indicator */}
				{currentBrand && workspaceId && isCollapsed && (
					<div className="p-3 flex justify-center">
						<CollapsedSubscriptionIndicator brand={currentBrand} workspaceId={workspaceId} />
					</div>
				)}

				{/* Profile */}
				{user.first_name && user.last_name && (
					<div className="p-2 border-t border-gray-200">
						<UserProfile first_name={user.first_name} last_name={user.last_name} email={user.email} avatar={user.avatar_url} collapsed={isCollapsed} />
					</div>
				)}
			</aside>
		</TooltipProvider>
	);
}
